import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export type UnitSystem = 'metric' | 'imperial';
export type ComplianceRegion = 'eu' | 'pl' | 'uk' | 'de' | 'fr' | 'es' | 'it' | 'us';

export interface UserPreferences {
  unitSystem: UnitSystem;
  defaultCurrency: string;
  defaultVatRate: number;
  language: string;
  onboardingCompleted: boolean;
  /** Branding — printed top-left of every PDF/DXF title block when set.
   *  Stored as base64 data URL (PNG/JPEG, kept under ~100 KB to stay
   *  inside localStorage limits). */
  companyLogoDataUrl?: string;
  /** Brand primary colour as #RRGGBB hex — fills the title-block header
   *  band; text colour auto-contrasted against it. */
  brandPrimaryColor?: string;
  /** Geographic compliance region — selects which national standards
   *  (BS / NF / DIN VDE / UNE / PN / CEI / ANSI/SIA) appear in the
   *  STANDARDS line of the title block. */
  complianceRegion?: ComplianceRegion;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  unitSystem: 'metric',
  defaultCurrency: 'USD',
  defaultVatRate: 23,
  language: 'en',
  onboardingCompleted: false,
};

const STORAGE_KEY = 'user_preferences';
const PREFERENCES_CHANGE_EVENT = 'user_preferences_changed';

// Internal hook — runs the real preferences logic ONCE at the Provider level.
// Direct callers should use `useUserPreferences()` (the public alias below)
// which reads from the Context. Without the Provider, every consumer used to
// open its own postgres_changes channel + Supabase fetch — same 20x-traffic
// + flicker pattern that bit useAuth (see comment in useAuth.tsx).
const useUserPreferencesImpl = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load preferences from localStorage on mount and set up real-time listener.
  // Guard each post-await `setState` with an isMounted flag so React 18 strict
  // mode (and rapid login/logout) doesn't fire "state update on unmounted
  // component" warnings + late writes that clobber a fresher mount.
  useEffect(() => {
    let isMounted = true;
    const loadPreferences = async () => {
      try {
        if (isMounted) setLoading(true);

        // Try to get from localStorage first (fast)
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (isMounted) setPreferences(parsed);
          } catch {
            // Invalid JSON, ignore
          }
        } else {
          if (isMounted) setPreferences(DEFAULT_PREFERENCES);
        }

        // If user is logged in, try to fetch from Supabase
        if (user?.id) {
          try {
            const { data, error } = await supabase
              .from('user_preferences')
              .select('*')
              .eq('user_id', user.id)
              .maybeSingle();

            if (!isMounted) return;

            if (!error && data) {
              // Branding fields (logo, brand colour, compliance region) are
              // localStorage-only for now — preserve whatever the user had
              // locally instead of clobbering with DB data that doesn't
              // carry those columns.
              const localExtras = (() => {
                try {
                  const raw = localStorage.getItem(STORAGE_KEY);
                  return raw ? (JSON.parse(raw) as UserPreferences) : undefined;
                } catch { return undefined; }
              })();
              const prefs: UserPreferences = {
                unitSystem: data.unit_system || DEFAULT_PREFERENCES.unitSystem,
                defaultCurrency: data.default_currency || DEFAULT_PREFERENCES.defaultCurrency,
                defaultVatRate: data.default_vat_rate ?? DEFAULT_PREFERENCES.defaultVatRate,
                language: data.language || DEFAULT_PREFERENCES.language,
                onboardingCompleted: data.onboarding_completed ?? DEFAULT_PREFERENCES.onboardingCompleted,
                companyLogoDataUrl: localExtras?.companyLogoDataUrl,
                brandPrimaryColor: localExtras?.brandPrimaryColor,
                complianceRegion: localExtras?.complianceRegion,
              };
              setPreferences(prefs);
              // Update localStorage with the Supabase data
              localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
            } else if (!error && !data) {
              // New user — no row in DB yet. Reset to defaults to avoid stale
              // localStorage from a previous account (e.g. onboardingCompleted: true)
              setPreferences(DEFAULT_PREFERENCES);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_PREFERENCES));
            } else if (error) {
              if (error.code === 'PGRST116' || error.message?.includes('not found')) {
                // Table might not exist yet - this is OK, will use localStorage
                console.debug('user_preferences table not yet created or no data found');
              } else {
                console.warn('Error fetching user preferences from Supabase:', {
                  message: error.message,
                  code: error.code,
                  details: error.details,
                });
              }
            }
          } catch (err) {
            console.error('Error fetching user preferences from Supabase:', err);
            // Fall back to localStorage data if available
          }
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadPreferences();
    return () => { isMounted = false; };
  }, [user?.id]);

  // Set up real-time listener for preferences changes
  useEffect(() => {
    if (!user?.id) return;

    const realtimeSubscription = supabase
      .channel(`public:user_preferences:user_id=eq.${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_preferences',
          filter: `user_id=eq.${user.id}`,
        },
        (payload: any) => {
          // Validate payload shape before trusting it. Supabase Realtime
          // only sends row fields when REPLICA IDENTITY = FULL is set on
          // the table — without that, payload.new arrives as `{}` and
          // every preference would reset to DEFAULT_PREFERENCES (USD,
          // metric, vat=23, etc.), then localStorage cross-tab sync
          // amplifies the corruption to other tabs. Require at least the
          // user_id to be present as a sanity check that the row payload
          // is genuinely populated.
          if (!payload?.new || typeof payload.new !== 'object' || payload.new.user_id !== user.id) {
            return;
          }
          // Also bail if every DB-backed field is undefined — REPLICA
          // IDENTITY DEFAULT delivers PK only, which would pass the
          // user_id check above but still clobber prefs with defaults.
          const hasAnyField =
            payload.new.unit_system !== undefined ||
            payload.new.default_currency !== undefined ||
            payload.new.default_vat_rate !== undefined ||
            payload.new.language !== undefined ||
            payload.new.onboarding_completed !== undefined;
          if (!hasAnyField) return;

          // Branding fields are localStorage-only — preserve them when
          // syncing the DB-backed columns.
          setPreferences((prev) => {
            const prefs: UserPreferences = {
              unitSystem: payload.new.unit_system || prev.unitSystem,
              defaultCurrency: payload.new.default_currency || prev.defaultCurrency,
              defaultVatRate: payload.new.default_vat_rate ?? prev.defaultVatRate,
              language: payload.new.language || prev.language,
              onboardingCompleted: payload.new.onboarding_completed ?? prev.onboardingCompleted,
              companyLogoDataUrl: prev.companyLogoDataUrl,
              brandPrimaryColor: prev.brandPrimaryColor,
              complianceRegion: prev.complianceRegion,
            };
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
            return prefs;
          });
        }
      )
      .subscribe();

    return () => {
      realtimeSubscription.unsubscribe();
    };
  }, [user?.id]);

  // Listen for preference changes from other component instances
  useEffect(() => {
    const handlePreferencesChange = (event: any) => {
      const newPrefs = event.detail;
      setPreferences(newPrefs);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
    };

    window.addEventListener(PREFERENCES_CHANGE_EVENT, handlePreferencesChange as EventListener);
    return () => {
      window.removeEventListener(PREFERENCES_CHANGE_EVENT, handlePreferencesChange as EventListener);
    };
  }, []);

  const updatePreferences = async (updates: Partial<UserPreferences>): Promise<boolean> => {
    try {
      setSaving(true);
      const newPreferences = { ...preferences, ...updates };

      // Immediately update state and localStorage for instant UI feedback
      setPreferences(newPreferences);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPreferences));

      // Emit custom event so other component instances can sync
      window.dispatchEvent(new CustomEvent(PREFERENCES_CHANGE_EVENT, { detail: newPreferences }));

      // If user is logged in, also update in Supabase
      if (user?.id) {
        try {
          const { data: upsertedData, error: upsertError } = await supabase
            .from('user_preferences')
            .upsert(
              {
                user_id: user.id,
                unit_system: newPreferences.unitSystem,
                default_currency: newPreferences.defaultCurrency,
                default_vat_rate: newPreferences.defaultVatRate,
                language: newPreferences.language,
                onboarding_completed: newPreferences.onboardingCompleted,
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'user_id' }
            )
            .select()
            .maybeSingle();

          if (upsertError) {
            console.error('Error upserting preferences:', {
              message: upsertError.message,
              code: upsertError.code,
              details: upsertError.details,
            });
            throw upsertError;
          }

          // Sync the data back from server to ensure consistency
          if (upsertedData) {
            const serverPrefs: UserPreferences = {
              unitSystem: upsertedData.unit_system || newPreferences.unitSystem,
              defaultCurrency: upsertedData.default_currency || newPreferences.defaultCurrency,
              defaultVatRate: upsertedData.default_vat_rate ?? newPreferences.defaultVatRate,
              language: upsertedData.language || newPreferences.language,
              onboardingCompleted: upsertedData.onboarding_completed ?? newPreferences.onboardingCompleted,
            };
            setPreferences(serverPrefs);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(serverPrefs));
            window.dispatchEvent(new CustomEvent(PREFERENCES_CHANGE_EVENT, { detail: serverPrefs }));
          }
        } catch (supabaseErr) {
          console.error('Supabase operation failed:', supabaseErr);
          // Keep the local update even if Supabase fails - it's saved in localStorage
          if (supabaseErr instanceof Error && supabaseErr.message.includes('user_preferences')) {
            console.warn('user_preferences table may not exist yet. Preferences saved locally.');
          }
        }
      }

      return true;
    } catch (err) {
      console.error('Error updating preferences:', {
        error: err,
        message: err instanceof Error ? err.message : String(err),
      });
      // Revert to previous preferences on error
      setPreferences(preferences);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const convertMeters = (meters: number): number => {
    if (preferences.unitSystem === 'imperial') {
      return meters * 3.28084; // meters to feet
    }
    return meters;
  };

  const convertFeet = (feet: number): number => {
    if (preferences.unitSystem === 'metric') {
      return feet / 3.28084; // feet to meters
    }
    return feet;
  };

  const getUnitLabel = (): string => {
    return preferences.unitSystem === 'metric' ? 'm' : 'ft';
  };

  return {
    preferences,
    loading,
    saving,
    updatePreferences,
    convertMeters,
    convertFeet,
    getUnitLabel,
  };
};

// ============================================================================
// React Context — single source of preferences state for the whole tree.
// Mirror of the AuthProvider pattern (see useAuth.tsx for the rationale).
// ============================================================================

type UserPreferencesValue = ReturnType<typeof useUserPreferencesImpl>;
const UserPreferencesContext = createContext<UserPreferencesValue | null>(null);

export const UserPreferencesProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useUserPreferencesImpl();
  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

// SSG fallback — prerender happens without a real Provider; return defaults
// so pages render without crashing. Mutating fns are no-ops in SSG.
const ssgFallback: UserPreferencesValue = {
  preferences: DEFAULT_PREFERENCES,
  loading: false,
  saving: false,
  updatePreferences: async () => false,
  convertMeters: (m: number) => m,
  convertFeet: (f: number) => f,
  getUnitLabel: () => 'm',
};

export const useUserPreferences = (): UserPreferencesValue => {
  const ctx = useContext(UserPreferencesContext);
  return ctx ?? ssgFallback;
};
